import React, { useState, useContext, useEffect } from "react";
import { Link as RouterLink, Redirect } from "react-router-dom";

import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Link from "@material-ui/core/Link";
import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import Alert from "@material-ui/lab/Alert";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import packageJson from "../../../package.json";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { onLicenseNotice, takeLicenseNotice } from "../../helpers/licenseNotice";
import logo from "../../assets/logo.png";

const { versionSystem, nomeEmpresa } = packageJson;


const Copyright = () => {
	return (
		<Typography variant="body2" color="primary" align="center">
			{"Copyright "}
 			<Link color="primary" href="#">
 				{ nomeEmpresa } - v { versionSystem }
 			</Link>{" "}
 			{new Date().getFullYear()}
 			{"."}
 		</Typography>
 	);
 };

const useStyles = makeStyles(theme => ({
	root: {
		width: "100vw",
		height: "100vh",
		//background: "linear-gradient(to right, #00aeef , #00aeef , #00aeef)",
		//backgroundImage: "url(https://i.imgur.com/CGby9tN.png)",
		// No tema escuro a cor primária é branca (#FFFFFF): de fundo, cansava a
		// vista. O escuro usa o cinza do app (palette.light = #333333).
		backgroundColor: theme.palette.type === "dark"
			? theme.palette.light.main
			: theme.palette.primary.main,
		backgroundRepeat: "no-repeat",
		backgroundSize: "100% 100%",
		backgroundPosition: "center",
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		textAlign: "center",
	},
	paper: {
		backgroundColor: theme.palette.login,
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		padding: "55px 30px",
		borderRadius: "12.5px",
	},
	avatar: {
		margin: theme.spacing(1),  
		backgroundColor: theme.palette.secondary.main,
	},
	form: {
		width: "100%", // Fix IE 11 issue.
		marginTop: theme.spacing(1),
	},
	submit: {
		margin: theme.spacing(3, 0, 2),
	},
	powered: {
		color: "white"
	}
}));

const Login = () => {
	const classes = useStyles();

	const [user, setUser] = useState({ email: "", password: "" });
	const [showPassword, setShowPassword] = useState(false);
	const [showSessionExpiredMessage, setShowSessionExpiredMessage] = useState(false);
	// "Excluir minha conta": flag gravada pelo DeleteAccountModal antes do logout
	const [showAccountDeletedMessage, setShowAccountDeletedMessage] = useState(false);
	// Licença Empsis inválida: null = sem aviso; "" = bloqueada sem motivo detalhado.
	const [licenseBlockedReason, setLicenseBlockedReason] = useState(null);

	const { handleLogin, isAuth, loading } = useContext(AuthContext);

	useEffect(() => {
		const accountDeleted = localStorage.getItem("accountDeleted") === "1";
		localStorage.removeItem("accountDeleted");
		if (accountDeleted) {
			setShowAccountDeletedMessage(true);
		}
	}, []);

	// Recado de licença inválida: gravado pelo interceptor do api.js, pelo
	// force-logout do socket (antes de vir para cá) ou pelo próprio handleLogin
	// (com esta tela aberta, por isso o evento).
	useEffect(() => {
		const readNotice = () => {
			const reason = takeLicenseNotice();
			if (reason !== null) {
				setLicenseBlockedReason(reason);
			}
		};
		readNotice();
		return onLicenseNotice(readNotice);
	}, []);

	// Verifica se deve mostrar mensagem de sessão expirada
	// Executado apenas uma vez no mount do componente
	useEffect(() => {
		// Pequeno delay para garantir que useAuth já processou
		const checkTimeout = setTimeout(() => {
			const sessionExpired = localStorage.getItem("sessionExpired") === "true";
			
			// Limpa o flag imediatamente para evitar mostrar novamente
			localStorage.removeItem("sessionExpired");
			
			// Só mostra mensagem se a flag estava setada
			if (sessionExpired) {
				setShowSessionExpiredMessage(true);
				
				// Remove a mensagem após 10 segundos
				const timer = setTimeout(() => {
					setShowSessionExpiredMessage(false);
				}, 10000);
				
				return () => clearTimeout(timer);
			}
		}, 100);
		
		return () => clearTimeout(checkTimeout);
	}, []);

	// Se já está autenticado, redireciona para /tickets
	if (isAuth && !loading) {
		return <Redirect to="/tickets" />;
	}

	const handleChangeInput = e => {
		setUser({ ...user, [e.target.name]: e.target.value });
	};

	const handlSubmit = e => {
		e.preventDefault();
		setLicenseBlockedReason(null);
		handleLogin(user);
	};

	return (
		<div className={classes.root}>
		<Container component="main" maxWidth="xs">
			<CssBaseline/>
			<div className={classes.paper}>
				<div>
					<img style={{ margin: "0 auto", width: "70%" }} src={logo} alt="Whats" />
				</div>
				{licenseBlockedReason !== null && (
					<Box mt={2} mb={2} width="100%">
						<Alert
							severity="error"
							onClose={() => setLicenseBlockedReason(null)}
							style={{ textAlign: "left" }}
						>
							<strong>{i18n.t("login.licenseBlocked.title")}</strong>
							{licenseBlockedReason && (
								<div>
									{i18n.t("login.licenseBlocked.reason", {
										reason: licenseBlockedReason,
										interpolation: { escapeValue: false },
									})}
								</div>
							)}
							<div>{i18n.t("login.licenseBlocked.contact")}</div>
						</Alert>
					</Box>
				)}
				{showSessionExpiredMessage && !showAccountDeletedMessage && licenseBlockedReason === null && (
					<Box mt={2} mb={2} width="100%">
						<Alert severity="warning" onClose={() => setShowSessionExpiredMessage(false)}>
							{i18n.t("login.sessionExpired")}
						</Alert>
					</Box>
				)}
				{showAccountDeletedMessage && (
					<Box mt={2} mb={2} width="100%">
						<Alert severity="success" onClose={() => setShowAccountDeletedMessage(false)}>
							{i18n.t("deleteAccountModal.loginBanner")}
						</Alert>
					</Box>
				)}
				{/*<Typography component="h1" variant="h5">
					{i18n.t("login.title")}
				</Typography>*/}
				<form className={classes.form} noValidate onSubmit={handlSubmit}>
					<TextField
						variant="outlined"
						margin="normal"
						required
						fullWidth
						id="email"
						label={i18n.t("login.form.email")}
						name="email"
						value={user.email}
						onChange={handleChangeInput}
						autoComplete="email"
						autoFocus
					/>
					<TextField
						variant="outlined"
						margin="normal"
						required
						fullWidth
						name="password"
						label={i18n.t("login.form.password")}
						type={showPassword ? "text" : "password"}
						id="password"
						value={user.password}
						onChange={handleChangeInput}
						autoComplete="current-password"
						InputProps={{
							endAdornment: (
								<InputAdornment position="end">
									<IconButton
										aria-label={i18n.t("login.form.showHidePassword")}
										onClick={() => setShowPassword(prev => !prev)}
										onMouseDown={e => e.preventDefault()}
										edge="end"
									>
										{showPassword ? <VisibilityOff /> : <Visibility />}
									</IconButton>
								</InputAdornment>
							),
						}}
					/>
					
					{/* <Grid container justify="flex-end">
					  <Grid item xs={6} style={{ textAlign: "right" }}>
						<Link component={RouterLink} to="/forgetpsw" variant="body2">
						  Esqueceu sua senha?
						</Link>
					  </Grid>
					</Grid>*/}
					
					<Button
						type="submit"
						fullWidth
						variant="contained"
						color="primary"
						className={classes.submit}
					>
						{i18n.t("login.buttons.submit")}
					</Button>
					{ <Grid container>
						<Grid item>
							<Link
								href="#"
								variant="body2"
								component={RouterLink}
								to="/signup"
							>
								{i18n.t("login.buttons.register")}
							</Link>
						</Grid>
					</Grid> }
				</form>
			
			</div>
			<Box mt={8}><Copyright /></Box>
		</Container>
		</div>
	);
};

export default Login;
